<!-- TopPriorityLanding.svelte - Prioritization & Focus Landing Page -->

<script>
  import { onMount } from "svelte";
  import { push } from 'svelte-spa-router';
  import ResultsChart from '../FocalPath/ResultsChart.svelte';
  import PriorityRanking from '../FocalPath/PriorityRanking.svelte';
  import cookieManager from '../utils/cookieManager.js';

  let showAppNameText = $state(false);
  let showCTAbutton = $state(false);
  let sectionsVisible = $state({});
  
  // Modal state for Founder Mode waitlist
  let showWaitlistModal = $state(false);
  let waitlistForm = $state({
    email: '',
    phone: '',
    contactMethod: 'email'
  });
  let isSubmitting = $state(false);
  let submitSuccess = $state(false);

  // Sample data for priority mockup (hero section)
  // Using PriorityRanking component format: { name, score }
  const heroPriorities = [
    { name: "Launch MVP", score: 85 },
    { name: "Content Marketing", score: 65 },
    { name: "Team Hiring", score: 45 },
    { name: "Office Setup", score: 25 },
    { name: "Logo Redesign", score: 15 }
  ];

  // Alternative data for solution section chart
  const solutionData = [0.95, 0.72, 0.58, 0.31, 0.18];
  const solutionLabels = [
    "Client Growth",
    "Product Dev",
    "Team Building", 
    "",
    ""
  ];
  const solutionColors = [
    "rgba(156,255,250,1.0)", // teal - top priority (full opacity)
    "rgba(156,255,250,0.7)", // teal - second priority
    "rgba(156,255,250,0.7)", // teal - third priority
    "rgba(150,150,150,0.5)", // gray - lower priority
    "rgba(150,150,150,0.5)"  // gray - lowest priority
  ];

  function startAnimations() {
    setTimeout(showAppName, 500);
    setTimeout(showCTA, 1000);
  }


  function showAppName() {
    showAppNameText = true;
  }

  function showCTA() {
    showCTAbutton = true;
  }

  function setupIntersectionObserver() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.dataset.section;
            sectionsVisible[sectionId] = true;
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    document.querySelectorAll('[data-section]').forEach(section => {
      observer.observe(section);
    });

    return observer;
  }

  onMount(() => {
    const id = setTimeout(startAnimations, 500);
    const observer = setupIntersectionObserver();
    
    return () => {
      clearTimeout(id);
      observer.disconnect();
    };
  });

  function goToPath() {
    push('/TopPriorityApp');
  }

  function scrollToSolution() {
    document.querySelector('.solution-section').scrollIntoView({ 
      behavior: 'smooth' 
    });
  }

  function openWaitlistModal() {
    showWaitlistModal = true;
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
  }

  function closeWaitlistModal() {
    showWaitlistModal = false;
    document.body.style.overflow = 'auto';
    // Reset form if user closes without submitting
    if (!submitSuccess) {
      waitlistForm = { email: '', phone: '', contactMethod: 'email' };
    }
  }

  async function handleWaitlistSubmit(event) {
    event.preventDefault();
    
    if (!waitlistForm.email.trim()) {
      alert('Please enter your email address');
      return;
    }

    isSubmitting = true;
    
    // Determine environment once for the entire function
    const isDevelopment = window.location.port === '5174' || window.location.hostname === 'localhost';
    
    try {
      // Prepare data for API call
      const waitlistData = {
        email: waitlistForm.email.trim(),
        phone: waitlistForm.phone.trim(),
        contactMethod: waitlistForm.contactMethod,
        userId: cookieManager.userId,
        sessionId: cookieManager.sessionId
      };

      // Call the waitlist API endpoint
      const apiUrl = isDevelopment 
        ? 'http://localhost:5000/api/waitlist' 
        : '/api/waitlist';

      // Add timeout (shorter for development, longer for production)
      const controller = new AbortController();
      const timeoutDuration = isDevelopment ? 5000 : 15000; // 5s dev, 15s production
      const timeoutId = setTimeout(() => controller.abort(), timeoutDuration);
        
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(waitlistData),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      let result;
      
      // Handle different response types (JSON vs HTML error pages)
      try {
        result = await response.json();
      } catch (parseError) {
        // If JSON parsing fails, it's likely an HTML error page (404, 500, etc.)
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }

      if (response.ok && result.status === 'success') {
        submitSuccess = true;
        
        // Close modal after 2 seconds
        setTimeout(() => {
          closeWaitlistModal();
          submitSuccess = false;
          waitlistForm = { email: '', phone: '', contactMethod: 'email' };
        }, 2000);
      } else {
        // Handle API errors
        const errorMessage = result.message || 'Failed to join waitlist. Please try again.';
        alert(errorMessage);
      }
      
    } catch (error) {
      // Handle different types of errors in development
      if (isDevelopment) {
        if (error.name === 'AbortError' || 
            error.message.includes('Failed to fetch') || 
            error.message.includes('The operation was aborted') ||
            error instanceof DOMException) {
          // Timeout or connection refused (Flask server not running) - this is expected in dev
          console.log('Development mode: Simulating waitlist signup (Flask server not available)');
          alert('Development mode: Flask server not running. Waitlist signup simulated successfully!');
          submitSuccess = true;
          setTimeout(() => {
            closeWaitlistModal();
            submitSuccess = false;
            waitlistForm = { email: '', phone: '', contactMethod: 'email' };
          }, 2000);
        } else if (error.message.includes('Server error: 404')) {
          // Server running but endpoint not found
          console.log('Development mode: Simulating waitlist signup (endpoint not found)');
          alert('Development mode: Waitlist signup simulated successfully! (No database connected)');
          submitSuccess = true;
          setTimeout(() => {
            closeWaitlistModal();
            submitSuccess = false;
            waitlistForm = { email: '', phone: '', contactMethod: 'email' };
          }, 2000);
        } else {
          // Only log unexpected errors
          console.error('Unexpected development error:', error);
          alert('Development error: ' + error.message);
        }
      } else {
        // Production errors should be logged
        console.error('Waitlist submission error:', error);
        alert('Something went wrong. Please check your connection and try again.');
      }
    } finally {
      isSubmitting = false;
    }
  }

  // Close modal when clicking outside
  function handleModalBackdropClick(event) {
    if (event.target === event.currentTarget) {
      closeWaitlistModal();
    }
  }

  // Handle keyboard events for modal backdrop
  function handleModalBackdropKeydown(event) {
    if (event.key === 'Escape') {
      closeWaitlistModal();
    }
  }
</script>

<main>
  <!-- 1. Hero Section -->
  <div class="hero-section">
    <div class="hero-content">
      <div class="hero-left">
        <div class="logo-container" class:fadeIn={showAppNameText}>
          <img 
            src="/Icons/TopPriority-Logo-Circles.svg" 
            alt="Top Priority Logo" 
            class="hero-logo"
          />
        </div>
        
        <h1 class="hero-headline" class:fadeIn={showAppNameText}>
          Protect your focus. Accelerate your progress.
        </h1>
        
        <p class="hero-subheadline" class:fadeIn={showAppNameText}>
          <i><strong>Top Priority</strong></i> helps you prioritize your projects, cut distractions, and build traction toward what truly matters.
        </p>
        

      </div>
      
      <div class="hero-right">
        
        <!-- Priority Ranking Mockup -->
        <div class="priority-chart" class:fadeIn={showCTAbutton}>
          <PriorityRanking 
            priorities={heroPriorities}
            title=""
            showScores={false}
            lowerPriorityTitle="Distractions: avoid until Top Priority achieved"
          />
        </div>
        
        <div class="hero-buttons" class:fadeIn={showCTAbutton}>
          <button class="primary-cta" onclick={goToPath}>
            Find My Top 3 Priorities
          </button>
          <button class="secondary-cta" onclick={scrollToSolution}>
            See How It Works
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- 2. Problem Section -->
  <div class="problem-section" data-section="problem" class:visible={sectionsVisible.problem}>
    <div class="container">
      <h2>You don't need more time — you need better focus.</h2>
      
      <div class="problem-grid">
        <div class="problem-item">
          <div class="problem-icon">⚙️</div>
          <h3>Overwhelmed by too many projects</h3>
          <p>Every opportunity feels urgent, but nothing gets the attention it deserves.</p>
        </div>
        
        <div class="problem-item">
          <div class="problem-icon">🔄</div>
          <h3>Constant context switching</h3>
          <p>Jumping between tasks kills momentum and fragments your mental energy.</p>
        </div>
        
        <div class="problem-item">
          <div class="problem-icon">💭</div>
          <h3>No clear sense of what drives results</h3>
          <p>Working hard but not sure if you're working on the right things.</p>
        </div>
      </div>
    </div>
  </div>

  <!-- 3. Solution Section -->
  <div class="solution-section" data-section="solution" class:visible={sectionsVisible.solution}>
    <div class="container">
      <h2>Turn chaos into clarity in three simple steps</h2>
      
      <div class="steps-timeline">
        <div class="step-item">
          <div class="step-number">📝</div>
          <h3>List your projects</h3>
          <p>Brain dump everything competing for your attention</p>
        </div>
        
        <div class="timeline-arrow">→</div>
        
        <div class="step-item">
          <div class="step-number">⚖️</div>
          <h3>Score them by impact & effort</h3>
          <p>Quick sliders reveal what truly moves the needle</p>
        </div>
        
        <div class="timeline-arrow">→</div>
        
        <div class="step-item">
          <div class="step-number">🎯</div>
          <!-- <h3>Visualize your <i>Top Priority</i> instantly</h3> -->
          <p>Visual ranking shows exactly where to invest your energy</p>
        </div>
      </div>
      
      <!-- Priority Results Chart for Solution Section -->
      <div class="solution-chart">
        <!-- <div class="solution-chart-header">📊 Your Prioritized Focus Path</div> -->
        <div class="solution-chart-wrapper">
          <ResultsChart 
            data={solutionData} 
            labels={solutionLabels} 
            colors={solutionColors}
            showStars={false}
            textPositionThreshold={0.25}
            showGridLines={false}
          />
        </div>
      </div>

      
      <button class="solution-cta" onclick={goToPath}>
        <!-- Try My Focus Session Free -->
         Start free — clarity in minutes
      </button>
      <p class="solution-subtext">no signup required</p>
    </div>
  </div>

  <!-- 4. Proof Section -->
  <div class="proof-section" data-section="proof" class:visible={sectionsVisible.proof}>
    <div class="container">
      <div class="proof-content">
        <div class="proof-left">
          <blockquote class="research-quote">
            "High performers spend 80% of their time on the 20% that truly matters."
          </blockquote>
          <cite>— McKinsey Research on Focus & Performance</cite>
        </div>
        
        <div class="proof-right">
          <div class="proof-cards">
            <div class="proof-card">
              <div class="proof-card-icon">📘</div>
              <p>Grounded in behavioral economics</p>
            </div>
            <div class="proof-card">
              <div class="proof-card-icon">📈</div>
              <p>Proven prioritization algorithms</p>
            </div>
            <div class="proof-card">
              <div class="proof-card-icon">🧠</div>
              <p>Designed by decision-science professionals</p>
            </div>
          </div>
        </div>
      </div>
      
      <div class="testimonials">
        <div class="testimonial">
          <p>"<strong>Top Priority</strong> helped me cut 7 side projects and double my results in 3 weeks."</p>
          <cite>— Sarah K., Startup Founder</cite>
        </div>
      </div>
    </div>
  </div>

  <!-- 5. Speed & Simplicity Section -->
  <div class="speed-section" data-section="speed" class:visible={sectionsVisible.speed}>
    <div class="container">
      <div class="speed-content">
        <div class="speed-left">
          <div class="speed-illustration">
            <div class="stopwatch-icon">⏱️</div>
            <div class="focus-beam">🎯</div>
          </div>
        </div>
        
        <div class="speed-right">
          <h2>Clarity before your next coffee break</h2>
          <ul class="speed-benefits">
            <li>No setup, no spreadsheets</li>
            <li>Visual dashboard summary</li>
            <li>100% private and personal</li>
          </ul>
        </div>
      </div>
    </div>
  </div>

  <!-- 6. Pricing Section -->
  <div class="pricing-section" data-section="pricing" class:visible={sectionsVisible.pricing}>
    <div class="container">
      <h2>How much is distraction costing you?</h2>
      
      <div class="pricing-grid">
        <div class="pricing-card">
          <h3>Starter</h3>
          <div class="price">Free</div>
          <ul>
            <!-- <li>1 prioritization session</li> -->
            <li>Personal or project focus</li>
          </ul>
          <button onclick={goToPath}>Get Started</button>
        </div>
        
        <!-- <div class="pricing-card featured">
          <div class="popular-badge">Most Popular</div>
          <h3>Pro Focus</h3>
          <div class="price">€9<span>/month</span></div>
          <ul>
            <li>Focus time tracking</li>
            <li>Goal dashboards</li>
          </ul>
          <button onclick={goToPath}>Start Pro Trial</button>
        </div>
         -->
        
        <div class="pricing-card">
          <h3>Founder Mode</h3>
          <div class="price">€49<span>/month</span></div>
          <ul>
            <li>Team prioritization</li>
            <li>Goal tracking</li>
            <li>Focus analytics</li>
          </ul>
          <button onclick={openWaitlistModal}>Launch faster</button>
        </div>
      </div>
      
      <p class="pricing-guarantee">Cancel anytime • 30-day refund guarantee</p>
    </div>
  </div>

  <!-- 7. Final CTA Section -->
  <div class="final-cta-section" data-section="final-cta" class:visible={sectionsVisible['final-cta']}>
    <div class="container">
      <h2>Focus is power.</h2>
      <p class="final-subtitle"><strong>Top Priority</strong> helps you use it wisely.</p>
      <button class="final-cta-button" onclick={goToPath}>
        Find My Top 3 Priorities Now
      </button>
    </div>
  </div>

  <!-- 8. Footer -->
  <footer class="footer" data-section="footer" class:visible={sectionsVisible.footer}>
    <div class="container">
      <div class="footer-columns">
        <div class="footer-column">
          <h4>About</h4>
          <a href="/science">Science</a>
          <a href="/contact">Contact</a>
        </div>
        <div class="footer-column">
          <h4>Resources</h4>
          <a href="/guides">Focus Guides</a>
          <a href="/blog">Blog</a>
          <a href="/terms">Terms</a>
        </div>
      </div>
      
      <div class="footer-bottom">
        <p>&copy; 2025 <strong>Top Priority</strong> — Built for clarity and focus.</p>
      </div>
    </div>
  </footer>
</main>

<!-- Waitlist Modal -->
{#if showWaitlistModal}
  <div 
    class="modal-backdrop" 
    onclick={handleModalBackdropClick}
    onkeydown={handleModalBackdropKeydown}
    role="dialog"
    aria-modal="true"
    aria-labelledby="modal-title"
    tabindex="-1"
  >
    <div class="modal-content">
      <button class="modal-close" onclick={closeWaitlistModal}>×</button>
      
      {#if submitSuccess}
        <div class="success-message">
          <div class="success-icon">✓</div>
          <h3>You're on the list!</h3>
          <p>We'll notify you when new Founder Mode slots become available.</p>
        </div>
      {:else}
        <div class="modal-header">
          <h3 id="modal-title">Join the Founder Mode Waitlist</h3>
          <p class="modal-subtitle">
            <strong>Top Priority</strong> is developed by a small team. To ensure the best possible experience, 
            Founder Mode subscriptions are limited and currently full.
          </p>
          <p class="modal-subtitle">
            Want to be notified when new slots become available? Let us know how to reach you:
          </p>
        </div>
        
        <form class="waitlist-form" onsubmit={handleWaitlistSubmit}>
          <div class="form-group">
            <label for="email">Email Address *</label>
            <input 
              type="email" 
              id="email" 
              bind:value={waitlistForm.email}
              placeholder="your@email.com"
              required
              disabled={isSubmitting}
            />
          </div>
          
          <div class="form-group">
            <label for="phone">Phone Number (optional)</label>
            <input 
              type="tel" 
              id="phone" 
              bind:value={waitlistForm.phone}
              placeholder="+1 (555) 123-4567"
              disabled={isSubmitting}
            />
          </div>
          
          <div class="form-group">
            <label for="contact-method">Preferred Contact Method</label>
            <select 
              id="contact-method" 
              bind:value={waitlistForm.contactMethod}
              disabled={isSubmitting}
            >
              <option value="email">Email</option>
              <option value="phone">Phone</option>
              <option value="both">Both</option>
            </select>
          </div>
          
          <button 
            type="submit" 
            class="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Adding to waitlist...' : 'Join Waitlist'}
          </button>
        </form>
      {/if}
    </div>
  </div>
{/if}

<style>
  main {
    font-family: "IBM Plex Mono", monospace;
    line-height: 1.6;
    color: #333;
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;
  }

  @media (min-width: 768px) {
    .container {
      padding: 0 2rem;
    }
  }

  /* Hero Section - Mobile First */
  .hero-section {
    min-height: 100vh;
    background: linear-gradient(135deg, #9cfffa 0%, #084b83 100%);
    color: white;
    display: flex;
    align-items: center;
    padding: 1rem 0;
  }

  .hero-content {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;
    display: flex;
    flex-direction: column;
    gap: 2rem;
    align-items: center;
    text-align: center;
  }

  /* Desktop enhancement */
  @media (min-width: 768px) {
    .hero-content {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 3rem;
      text-align: left;
      padding: 0 2rem;
    }
  }

  @media (min-width: 1024px) {
    .hero-content {
      gap: 4rem;
    }
  }

  .hero-headline {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
    line-height: 1.2;
    opacity: 0;
  }

  .hero-subheadline {
    font-size: 1rem;
    margin-bottom: 2rem;
    opacity: 0;
    line-height: 1.4;
  }

  .hero-subheadline strong {
    color: inherit;
    font-weight: 700;
  }

  /* Logo Styles */
  .logo-container {
    display: flex;
    justify-content: center;
    margin-bottom: 1.5rem;
    opacity: 0;
  }

  .hero-logo {
    height: 80px;
    width: auto;
    filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.1));
  }

  @media (min-width: 768px) {
    .logo-container {
      justify-content: flex-start;
      margin-bottom: 2rem;
    }
    
    .hero-logo {
      height: 100px;
    }
  }

  @media (min-width: 1024px) {
    .hero-logo {
      height: 120px;
    }
  }

  /* Desktop enhancement */
  @media (min-width: 768px) {
    .hero-headline {
      font-size: 2.8rem;
      margin-bottom: 1.5rem;
    }
    
    .hero-subheadline {
      font-size: 1.2rem;
      margin-bottom: 2.5rem;
    }
  }

  @media (min-width: 1024px) {
    .hero-headline {
      font-size: 3.5rem;
    }
    
    .hero-subheadline {
      font-size: 1.3rem;
    }
  }

  .hero-buttons {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
    max-width: 320px;
    opacity: 0;
  }

  .primary-cta {
    background: white;
    color: #084b83;
    border: none;
    padding: 1rem 1.5rem;
    font-size: 1rem;
    font-weight: 600;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    min-height: 48px;
    width: 100%;
  }

  .secondary-cta {
    background: transparent;
    color: white;
    border: 2px solid white;
    padding: 1rem 1.5rem;
    font-size: 1rem;
    font-weight: 600;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    min-height: 48px;
    width: 100%;
  }

  /* Desktop enhancement */
  @media (min-width: 768px) {
    .hero-buttons {
      flex-direction: row;
      max-width: none;
      width: auto;
    }
    
    .primary-cta,
    .secondary-cta {
      padding: 1.2rem 2rem;
      font-size: 1.1rem;
      width: auto;
    }
  }

  .primary-cta:hover {
    background: #ffec6c;
    transform: translateY(-2px);
  }

  .secondary-cta:hover {
    background: white;
    color: #084b83;
  }

  .hero-right {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
    width: 100%;
  }

  @media (min-width: 768px) {
    .hero-right {
      gap: 2rem;
    }
  }


  .priority-chart {
    padding: 1rem;
    border-radius: 15px;
    width: 100%;
    max-width: 100%;
    opacity: 0;
  }

  @media (min-width: 768px) {
    .priority-chart {
      max-width: 450px;
      padding: 1.5rem;
    }
  }

  /* Problem Section - Mobile First */
  .problem-section {
    background: #1e1e1e;
    color: white;
    padding: 3rem 0;
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.8s ease-out;
  }

  @media (min-width: 768px) {
    .problem-section {
      padding: 5rem 0;
    }
  }

  .problem-section.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .problem-section h2 {
    font-size: 1.8rem;
    text-align: center;
    margin-bottom: 2rem;
    line-height: 1.3;
  }

  @media (min-width: 768px) {
    .problem-section h2 {
      font-size: 2.5rem;
      margin-bottom: 3rem;
    }
  }

  .problem-grid {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  @media (min-width: 768px) {
    .problem-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      gap: 2rem;
    }
  }

  .problem-item {
    text-align: center;
    padding: 1.5rem;
    background: rgba(255,255,255,0.1);
    border-radius: 15px;
  }

  @media (min-width: 768px) {
    .problem-item {
      padding: 2rem;
    }
  }

  .problem-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
  }

  .problem-item h3 {
    font-size: 1.1rem;
    margin-bottom: 0.8rem;
    color: #9cfffa;
    line-height: 1.3;
  }

  @media (min-width: 768px) {
    .problem-item h3 {
      font-size: 1.3rem;
      margin-bottom: 1rem;
    }
  }

  /* Solution Section - Mobile First */
  .solution-section {
    padding: 3rem 0;
    background: white;
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.8s ease-out;
  }

  @media (min-width: 768px) {
    .solution-section {
      padding: 5rem 0;
    }
  }

  .solution-section.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .solution-section h2 {
    font-size: 1.8rem;
    text-align: center;
    margin-bottom: 2rem;
    color: #333;
    line-height: 1.3;
  }

  @media (min-width: 768px) {
    .solution-section h2 {
      font-size: 2.5rem;
      margin-bottom: 3rem;
    }
  }

  .steps-timeline {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;
    margin-bottom: 2rem;
  }

  @media (min-width: 768px) {
    .steps-timeline {
      flex-direction: row;
      justify-content: center;
      margin-bottom: 3rem;
      flex-wrap: wrap;
    }
  }

  .step-item {
    text-align: center;
    max-width: 280px;
    width: 100%;
  }

  @media (min-width: 768px) {
    .step-item {
      max-width: 250px;
      width: auto;
    }
  }

  .step-number {
    width: 80px;
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 3rem;
    margin: 0 auto 1rem auto;
  }

  .timeline-arrow {
    font-size: 2rem;
    color: #084b83;
    transform: rotate(90deg);
  }

  @media (min-width: 768px) {
    .timeline-arrow {
      transform: rotate(0deg);
    }
  }


  .solution-cta {
    background: #9cfffa;
    color: #084b83;
    border: none;
    padding: 1rem 2rem;
    font-size: 1rem;
    font-weight: 600;
    border-radius: 12px;
    cursor: pointer;
    display: block;
    margin: 0 auto;
    transition: all 0.3s ease;
    min-height: 48px;
    width: 100%;
    max-width: 320px;
  }

  @media (min-width: 768px) {
    .solution-cta {
      padding: 1.2rem 2.5rem;
      font-size: 1.2rem;
      width: auto;
      max-width: none;
    }
  }

  .solution-cta:hover {
    background: #ffec6c;
    transform: translateY(-2px);
  }

  .solution-subtext {
    text-align: center;
    font-size: 0.85rem;
    color: #666;
    margin-top: 0.5rem;
    opacity: 0.8;
    font-style: italic;
  }

  .solution-chart {
    margin: 2rem auto;
    padding: 1.5rem;
    background: rgba(156, 255, 250, 0.05);
    border-radius: 15px;
    border: 1px solid rgba(156, 255, 250, 0.2);
    max-width: 500px;
  }


  .solution-chart-wrapper {
    background: white;
    border-radius: 12px;
    padding: 1rem;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  }

  @media (min-width: 768px) {
    .solution-chart {
      padding: 2rem;
    }
    
    .solution-chart-wrapper {
      padding: 1.5rem;
    }
  }

  /* Proof Section - Mobile First */
  .proof-section {
    background: #f0f8ff;
    padding: 3rem 0;
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.8s ease-out;
  }

  @media (min-width: 768px) {
    .proof-section {
      padding: 5rem 0;
    }
  }

  .proof-section.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .proof-content {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    margin-bottom: 2rem;
    text-align: center;
  }

  @media (min-width: 768px) {
    .proof-content {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 3rem;
      margin-bottom: 3rem;
      align-items: center;
      text-align: left;
    }
  }

  .research-quote {
    font-size: 1.2rem;
    font-style: italic;
    color: #084b83;
    margin-bottom: 1rem;
    line-height: 1.4;
  }

  @media (min-width: 768px) {
    .research-quote {
      font-size: 1.5rem;
    }
  }

  .proof-cards {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .proof-card {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    background: white;
    border-radius: 10px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  }

  .proof-card-icon {
    font-size: 1.5rem;
  }

  .testimonials {
    text-align: center;
  }

  .testimonial {
    background: white;
    padding: 2rem;
    border-radius: 15px;
    max-width: 600px;
    margin: 0 auto;
    box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  }

  .testimonial p {
    font-size: 1.1rem;
    font-style: italic;
    margin-bottom: 1rem;
    color: #333;
  }

  /* Speed Section - Mobile First */
  .speed-section {
    padding: 3rem 0;
    background: white;
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.8s ease-out;
  }

  @media (min-width: 768px) {
    .speed-section {
      padding: 5rem 0;
    }
  }

  .speed-section.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .speed-content {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    text-align: center;
  }

  @media (min-width: 768px) {
    .speed-content {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 3rem;
      align-items: center;
      text-align: left;
    }
  }

  .speed-illustration {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 2rem;
    font-size: 4rem;
  }

  .speed-right h2 {
    font-size: 1.8rem;
    margin-bottom: 1rem;
    color: #333;
    line-height: 1.3;
  }

  @media (min-width: 768px) {
    .speed-right h2 {
      font-size: 2.2rem;
      margin-bottom: 1.5rem;
    }
  }

  .speed-benefits {
    list-style: none;
    padding: 0;
  }

  .speed-benefits li {
    padding: 0.8rem 0;
    font-size: 1.1rem;
    color: #666;
  }

  .speed-benefits li:before {
    content: "✓";
    color: #9cfffa;
    font-weight: bold;
    margin-right: 0.8rem;
  }

  /* Pricing Section - Mobile First */
  .pricing-section {
    background: #f8f9fa;
    padding: 3rem 0;
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.8s ease-out;
  }

  @media (min-width: 768px) {
    .pricing-section {
      padding: 5rem 0;
    }
  }

  .pricing-section.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .pricing-section h2 {
    font-size: 1.8rem;
    text-align: center;
    margin-bottom: 2rem;
    color: #333;
    line-height: 1.3;
  }

  @media (min-width: 768px) {
    .pricing-section h2 {
      font-size: 2.5rem;
      margin-bottom: 3rem;
    }
  }

  .pricing-grid {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    margin-bottom: 2rem;
  }

  @media (min-width: 768px) {
    .pricing-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      gap: 2rem;
    }
  }

  .pricing-card {
    background: white;
    border-radius: 15px;
    padding: 2.5rem 2rem;
    text-align: center;
    box-shadow: 0 4px 15px rgba(0,0,0,0.1);
    position: relative;
    transition: transform 0.3s ease;
  }

  .pricing-card:hover {
    transform: translateY(-5px);
  }



  .pricing-card h3 {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
    color: #333;
  }

  .price {
    font-size: 2.5rem;
    font-weight: 700;
    color: #084b83;
    margin-bottom: 1.5rem;
  }

  .price span {
    font-size: 1rem;
    color: #666;
  }

  .pricing-card ul {
    list-style: none;
    padding: 0;
    margin: 0 0 2rem 0;
  }

  .pricing-card li {
    padding: 0.5rem 0;
    color: #666;
  }

  .pricing-card li:before {
    content: "✓";
    color: #9cfffa;
    font-weight: bold;
    margin-right: 0.5rem;
  }

  .pricing-card button {
    width: 100%;
    padding: 1rem;
    border: 2px solid #084b83;
    background: transparent;
    color: #084b83;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-weight: 600;
    min-height: 48px;
    font-size: 1rem;
  }

  .pricing-card button:hover {
    background: #084b83;
    color: white;
  }

  .pricing-guarantee {
    text-align: center;
    color: #666;
    font-size: 0.9rem;
  }

  /* Final CTA Section - Mobile First */
  .final-cta-section {
    background: #9cfffa;
    color: #084b83;
    padding: 3rem 0;
    text-align: center;
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.8s ease-out;
  }

  @media (min-width: 768px) {
    .final-cta-section {
      padding: 5rem 0;
    }
  }

  .final-cta-section.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .final-cta-section h2 {
    font-size: 2rem;
    margin-bottom: 1rem;
    line-height: 1.2;
  }

  @media (min-width: 768px) {
    .final-cta-section h2 {
      font-size: 3rem;
    }
  }

  .final-subtitle {
    font-size: 1.1rem;
    margin-bottom: 1.5rem;
    opacity: 0.8;
    line-height: 1.4;
  }

  @media (min-width: 768px) {
    .final-subtitle {
      font-size: 1.3rem;
      margin-bottom: 2rem;
    }
  }

  .final-cta-button {
    background: #084b83;
    color: white;
    border: none;
    padding: 1rem 2rem;
    font-size: 1.1rem;
    font-weight: 700;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-bottom: 1rem;
    min-height: 48px;
    width: 100%;
    max-width: 320px;
  }

  @media (min-width: 768px) {
    .final-cta-button {
      padding: 1.5rem 3rem;
      font-size: 1.3rem;
      width: auto;
      max-width: none;
    }
  }

  .final-cta-button:hover {
    background: #063a6b;
    transform: translateY(-3px);
  }


  /* Ensure app name emphasis inherits proper colors */
  .final-subtitle strong,
  .testimonial strong,
  .footer strong {
    color: inherit;
    font-weight: 700;
  }

  /* Footer - Mobile First */
  .footer {
    background: #084b83;
    color: white;
    padding: 2rem 0 1rem;
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.8s ease-out;
  }

  @media (min-width: 768px) {
    .footer {
      padding: 3rem 0 1rem;
    }
  }

  .footer.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .footer-columns {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    margin-bottom: 2rem;
    text-align: center;
  }

  @media (min-width: 768px) {
    .footer-columns {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 2rem;
      text-align: left;
    }
  }

  .footer-column h4 {
    margin-bottom: 1rem;
    color: #9cfffa;
  }

  .footer-column a {
    display: block;
    color: rgba(255,255,255,0.8);
    text-decoration: none;
    margin-bottom: 0.5rem;
    transition: color 0.3s ease;
  }

  .footer-column a:hover {
    color: #9cfffa;
  }

  .footer-bottom {
    text-align: center;
    padding-top: 2rem;
    border-top: 1px solid rgba(255,255,255,0.2);
    color: rgba(255,255,255,0.7);
  }

  /* Animations */
  .fadeIn {
    animation: fadeIn 0.8s forwards;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }



  /* Mobile-first responsive design complete - no old breakpoints needed */
  
  /* Additional mobile optimizations */

  /* Modal Styles */
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
    box-sizing: border-box;
  }

  .modal-content {
    background: white;
    border-radius: 15px;
    padding: 2rem;
    max-width: 500px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    animation: modalSlideIn 0.3s ease-out;
  }

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  .modal-close {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: #666;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.2s ease;
  }

  .modal-close:hover {
    background: #f0f0f0;
    color: #333;
  }

  .modal-header h3 {
    color: #084b83;
    font-size: 1.5rem;
    margin-bottom: 1rem;
    line-height: 1.3;
  }

  .modal-subtitle {
    color: #666;
    line-height: 1.5;
    margin-bottom: 1rem;
  }

  .modal-subtitle:last-of-type {
    margin-bottom: 1.5rem;
  }

  .modal-subtitle strong {
    color: #084b83;
    font-weight: 700;
  }

  .waitlist-form {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .form-group label {
    font-weight: 600;
    color: #333;
    font-size: 0.9rem;
  }

  .form-group input,
  .form-group select {
    padding: 0.8rem;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 1rem;
    transition: border-color 0.2s ease;
    font-family: inherit;
  }

  .form-group input:focus,
  .form-group select:focus {
    outline: none;
    border-color: #9cfffa;
  }

  .form-group input:disabled,
  .form-group select:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }

  .submit-button {
    background: #9cfffa;
    color: #084b83;
    border: none;
    padding: 1rem 1.5rem;
    font-size: 1rem;
    font-weight: 600;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-top: 0.5rem;
    font-family: inherit;
  }

  .submit-button:hover:not(:disabled) {
    background: #7de8e0;
    transform: translateY(-1px);
  }

  .submit-button:disabled {
    background: #ccc;
    color: #666;
    cursor: not-allowed;
    transform: none;
  }

  .success-message {
    text-align: center;
    padding: 1rem 0;
  }

  .success-icon {
    font-size: 3rem;
    color: #4CAF50;
    margin-bottom: 1rem;
  }

  .success-message h3 {
    color: #084b83;
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }

  .success-message p {
    color: #666;
    line-height: 1.5;
  }

  /* Mobile optimizations for modal */
  @media (max-width: 480px) {
    .modal-content {
      padding: 1.5rem;
      margin: 0.5rem;
    }
    
    .modal-header h3 {
      font-size: 1.3rem;
      padding-right: 2rem;
    }
    
    .form-group input,
    .form-group select {
      padding: 0.7rem;
    }
  }
</style>
